import { useCallback, useEffect, useRef, useState } from "react";
import { Body } from "../../../components/main-body/body";
import { Button, Progress, Tag, Alert, Table, Space, Input } from "antd";
import ApiArticleCoverBackfill from "../../../api/article-cover-backfill";

// Minimal systemAdmin control panel for the article cover → S3 backfill.
// Start/restart it, watch live progress, and inspect per-row errors. The actual
// work runs in the backend; this page just triggers it and polls status.
const POLL_MS = 2000;

const STATUS_COLOR = {
  idle: "default",
  running: "processing",
  done: "success",
  failed: "error",
};

// Ids pasted one per line, or separated by commas / spaces.
const parsePageIds = (text) =>
  [...new Set(text.split(/[\s,]+/).filter(Boolean))];

const ArticleCoverBackfill = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageIdsText, setPageIdsText] = useState("");
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await ApiArticleCoverBackfill.getStatus();
      setStatus(res.data.status);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Failed to load status");
    }
  }, []);

  // Poll while the page is open; the backend keeps state across refreshes so
  // you can reopen this page and still see a run in progress.
  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchStatus]);

  const handleStart = async () => {
    setLoading(true);
    try {
      await ApiArticleCoverBackfill.start(parsePageIds(pageIdsText));
      await fetchStatus();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Failed to start");
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    setLoading(true);
    try {
      await ApiArticleCoverBackfill.stop();
      await fetchStatus();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Failed to stop");
    } finally {
      setLoading(false);
    }
  };

  const isRunning = status?.status === "running";
  const pageIdCount = parsePageIds(pageIdsText).length;
  const total = status?.total || 0;
  const processed = status?.processed || 0;
  const percent = total > 0 ? Math.round((processed / total) * 100) : 0;

  const errorColumns = [
    { title: "Page ID", dataIndex: "pageId", key: "pageId", width: 340 },
    { title: "Error", dataIndex: "message", key: "message" },
  ];

  return (
    <Body header={{ title: "Article cover backfill" }} scroll>
      <div style={{ maxWidth: 920, padding: 16 }}>
        <p style={{ color: "#6C6C89", marginBottom: 16 }}>
          Uploads existing article cover images from the database (base64) to S3
          and fills <code>imageKey</code> / <code>imageThumbKey</code>. Idempotent:
          rows already migrated are skipped, so it is safe to start again if a
          run is interrupted.
        </p>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: "#6C6C89", marginBottom: 4 }}>
            Article IDs (optional). Only these articles are processed, every
            version of each included. Leave empty to process all articles.
          </div>
          <Input.TextArea
            rows={4}
            value={pageIdsText}
            onChange={(e) => setPageIdsText(e.target.value)}
            placeholder="One id per line, or separated by commas"
            disabled={isRunning}
          />
        </div>

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          title="Progress is tracked in the backend instance that ran it. With multiple instances, open this from the same one; a restart clears the status (the work itself is idempotent, so re-running is safe)."
        />

        {status && status.s3Enabled === false && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            title="S3 uploads are disabled in this environment — nothing is migrated here. Run the backfill where S3 is configured (staging / production)."
          />
        )}

        {error && (
          <Alert
            type="error"
            showIcon
            title={error}
            style={{ marginBottom: 16 }}
          />
        )}

        <Space style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            loading={loading}
            disabled={isRunning}
            onClick={handleStart}
          >
            {status?.status === "done" || status?.status === "failed"
              ? "Restart"
              : "Start"}
            {pageIdCount > 0 && ` for ${pageIdCount} article${pageIdCount === 1 ? "" : "s"}`}
          </Button>
          <Button danger disabled={!isRunning} onClick={handleStop}>
            Stop
          </Button>
          <Button onClick={fetchStatus}>Refresh</Button>
        </Space>

        <div style={{ marginBottom: 12 }}>
          Status:{" "}
          <Tag color={STATUS_COLOR[status?.status] || "default"}>
            {status?.status || "—"}
          </Tag>
          {status?.startedAt && (
            <span style={{ color: "#6C6C89", fontSize: 13 }}>
              {status.pageIds?.length
                ? `Scope: ${status.pageIds.length} article id(s)`
                : "Scope: all articles"}
            </span>
          )}
        </div>

        {status?.notFound?.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            title={`No article with these ids: ${status.notFound.join(", ")}`}
          />
        )}

        <Progress percent={percent} status={isRunning ? "active" : undefined} />

        <div style={{ display: "flex", gap: 24, margin: "16px 0", flexWrap: "wrap" }}>
          <Stat label="Total" value={total} />
          <Stat label="Processed" value={processed} />
          <Stat label="Migrated" value={status?.migrated || 0} />
          <Stat label="Skipped" value={status?.skipped || 0} />
          <Stat label="Failed" value={status?.failed || 0} color={status?.failed ? "#cf1322" : undefined} />
        </div>

        <div style={{ color: "#6C6C89", fontSize: 13, marginBottom: 16 }}>
          {status?.startedAt && <div>Started: {status.startedAt}</div>}
          {status?.finishedAt && <div>Finished: {status.finishedAt}</div>}
          {status?.lastError && (
            <div style={{ color: "#cf1322" }}>Last error: {status.lastError}</div>
          )}
        </div>

        {status?.errors?.length > 0 && (
          <>
            <h4>Row errors ({status.errors.length})</h4>
            <Table
              size="small"
              rowKey={(r, i) => `${r.pageId}-${i}`}
              columns={errorColumns}
              dataSource={status.errors}
              pagination={false}
            />
          </>
        )}
      </div>
    </Body>
  );
};

const Stat = ({ label, value, color }) => (
  <div>
    <div style={{ fontSize: 12, color: "#6C6C89" }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 600, color }}>{value}</div>
  </div>
);

export default ArticleCoverBackfill;
